import PropTypes from 'prop-types'
import { Progress, BubbleTooltip } from '@/components'
import useCurrentMonthSpending from '@/hooks/use-current-month-spending'
import { HelpCircle, Info } from 'lucide-react'

const SpendingProgressBar = ({ maxAmount = 500, hideTitle = false, className = '' }) => {
  const { isLoading, data, isError } = useCurrentMonthSpending()

  if (isLoading) {
    return (
      <div className={`w-full ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={`flex flex-col gap-2 w-full ${className}`}>
        {!hideTitle && (
          <div className="flex justify-between items-center max-md:hidden">
            <span className="text-base font-semibold text-neutral-700">Spend Tracker</span>
          </div>
        )}
        <Progress value={0} indicatorColor="bg-[#26C7B8] rounded-r-full" className="h-2 bg-neutral-50" />
        <div className="flex items-center gap-1 text-[11px] leading-tight text-neutral-700">
          <Info className="w-7 h-7 text-black mr-1" />
          <div>We couldn&rsquo;t calculate your current spend at this time. Please try again later.</div>
        </div>
      </div>
    )
  }

  const currentAmount = data?.totalAmount || 0
  const percentage = Math.min((currentAmount / maxAmount) * 100, 100)
  const remainingAmount = Math.max(0, maxAmount - currentAmount)
  const isMaxedOut = currentAmount >= maxAmount

  const tooltipContent = isMaxedOut
    ? `You've reached the $${maxAmount} minimum and unlocked rewards on your car payment!`
    : `You're $${Math.ceil(remainingAmount)} away from unlocking rewards on your car payment.`

  return (
    <div className={`flex flex-col gap-2 w-full ${className}`}>
      {!hideTitle && (
        <div className="flex justify-between items-center max-md:hidden">
          <span className="text-base font-semibold text-neutral-700">Spend Tracker</span>
        </div>
      )}
      <Progress
        value={percentage}
        indicatorColor={
          isMaxedOut
            ? 'bg-[--java-300] rounded-r-full'
            : 'bg-gradient-to-r from-[--java-500] to-[--java-300] rounded-r-full'
        }
        className="h-2 bg-[#E0E3E6]"
      />
      <div className="flex justify-between items-center">
        <span className="text-sm md:text-base font-medium text-neutral-500 max-md:text-neutral-700">
          ${Math.floor(Math.min(currentAmount, maxAmount))} spent
        </span>
        <div className="flex items-center gap-1">
          <span className="text-sm md:text-base font-medium text-neutral-900">${Math.ceil(remainingAmount)} left</span>
          <BubbleTooltip
            button={<HelpCircle className="w-2.5 h-2.5 text-gray-400" />}
            content={tooltipContent}
            direction="top"
          />
        </div>
      </div>
    </div>
  )
}

SpendingProgressBar.propTypes = {
  maxAmount: PropTypes.number,
  className: PropTypes.string,
  hideTitle: PropTypes.bool,
}

export default SpendingProgressBar
